import type { ScreenBase } from "@frui.ts/screens/dist";
import { Router } from "@frui.ts/screens/dist";
import { Modules } from "config";
import { computed, observable } from "mobx";
import LocalizationService from "services/localizationService";
import EventBus, { subLedgerReportGenerated } from "services/eventBus";
import SecurityService from "services/securityService";
import ListViewModel from "viewModels/listViewModel";
import SubLedgerReportResponse from "../../../entities/subLedgerReportResponse";
import AccountingSubLedgerReportsRepository from "../../../repositories/accountingSubLedgerReportsRepository";
import { bound } from "@frui.ts/helpers";
import type { PropertyName } from "@frui.ts/helpers";
import download from "downloadjs";
import SubLedgerReportSearchRequest from "../../../entities/subLedgerReportSearchRequest";
import NotificationService from "../../../services/notificationService";

const navigationName = "list";

@Router.registerRoute({ route: navigationName, name: Router.Self })
export default class SubLedgerListViewModel extends ListViewModel<
  SubLedgerReportResponse,
  ScreenBase,
  ScreenBase,
  SubLedgerReportSearchRequest
> {
  readonly modelName = "subLedgerReportResponse";

  @observable downloadInProgress: Map<string, boolean> = new Map();

  public onNewReportClick?: () => void;

  constructor(
    protected localizationService: LocalizationService,
    protected eventBus: EventBus,
    protected securityService: SecurityService,
    protected repository: AccountingSubLedgerReportsRepository,
    protected notificationService: NotificationService
  ) {
    super();
    this.navigationName = navigationName;
  }

  onInitialize(): Promise<any> | void {
    this.unsubscriptions.push(this.eventBus.subscribe(subLedgerReportGenerated, this.loadData));
    return super.onInitialize();
  }

  @computed
  get canMakeReport() {
    return this.securityService.allowTo("finance.reports", "write");
  }

  protected createFilter(): SubLedgerReportSearchRequest {
    return new SubLedgerReportSearchRequest();
  }

  formatReportName(item: SubLedgerReportResponse): string {
    const createdAt = new Date(item.createdAt);

    return `nahled_${createdAt.getFullYear()}_${(createdAt.getMonth() + 1).toString().padStart(2, "0")}_${createdAt
      .getDate()
      .toString()
      .padStart(2, "0")}`;
  }

  @bound
  async onDownloadClick(item: SubLedgerReportResponse) {
    try {
      this.downloadInProgress.set(item.id, true);
      const response = await this.repository.downloadReport(item.id);
      this.downloadInProgress.set(item.id, false);
      download(response.data, `${this.formatReportName(item)}.xlsx`);

      await this.loadData();
    } catch (e) {}
  }

  translate(code: string, size?: number) {
    return this.localizationService.translateModule(Modules.finance, `subLedger.${code}`, size);
  }

  translateFilterAttribute(attribute: PropertyName<SubLedgerReportSearchRequest>, size?: number) {
    return this.localizationService.translateAttribute("subLedgerReportSearchRequest", attribute, size);
  }
}
